import React from "react";
import "../../../AllComponentCSS.css";
import CodeCopyBlock from "../../../CodeCopyBlock";

export default function RWD_Intro() {
  return (
    <div className="tutorial-container">
      {/* Hero Section */}
      <div
        style={{
          background: "linear-gradient(135deg, #06b6d4, #0284c7)",
          color: "white",
          padding: "2rem",
          borderRadius: "16px",
          marginBottom: "2rem",
          textAlign: "center",
        }}
      >
        <h1 style={{ fontSize: "2.5rem", fontWeight: "bold" }}>
          📱 Responsive Web Design (RWD) Introduction
        </h1>
        <p style={{ fontSize: "1.2rem", marginTop: "1rem" }}>
          Responsive Web Design (RWD) makes websites look great and work well
          across all devices — desktops, tablets, and smartphones.
        </p>
        <img
          src="https://www.w3schools.com/css/rwd_desktop.png"
          alt="Responsive Design Example"
          style={{
            maxWidth: "500px",
            margin: "1.5rem auto 0",
            borderRadius: "12px",
            boxShadow: "0 6px 20px rgba(0,0,0,0.2)",
          }}
        />
      </div>

      {/* Intro */}
      <p className="tutorial-intro">
        Responsive Web Design (RWD) is about building web pages that adapt to
        different screen sizes and devices. Instead of creating separate
        websites for desktop and mobile, RWD uses <b>flexible layouts</b>,{" "}
        <b>media queries</b>, and <b>fluid images</b> to ensure a consistent
        user experience everywhere.
      </p>

      {/* Key Points */}
      <div
        style={{
          background: "linear-gradient(135deg, #bae6fd, #7dd3fc)",
          padding: "1.2rem",
          paddingLeft: "35px",
          borderRadius: "12px",
          marginBottom: "2rem",
        }}
      >
        <h2 style={{ color: "#075985" }}>🔑 Key Points</h2>
        <ul className="tutorial-list-points">
          <li>
            RWD adapts a website’s layout depending on the{" "}
            <b>screen size & resolution</b>.
          </li>
          <li>
            Uses <code>@media</code> queries to apply styles for specific screen
            widths.
          </li>
          <li>
            Flexible <b>grids</b> and <b>fluid images</b> help adjust content
            automatically.
          </li>
          <li>
            RWD eliminates the need for multiple versions (desktop, mobile,
            tablet).
          </li>
          <li>
            Improves <b>user experience (UX)</b> and is crucial for{" "}
            <b>SEO rankings</b>.
          </li>
        </ul>
      </div>

      {/* Example 1: Media Query */}
      <h2 className="section-title">1️⃣ Example: Media Query</h2>
      <CodeCopyBlock
        code={`/* Apply styles for screens smaller than 600px */
@media (max-width: 600px) {
  body {
    background-color: lightblue;
  }
}`}
      />
      <p>
        In this example, the background changes to light blue on smaller
        screens.
      </p>

      {/* Example 2: Flexible Grid */}
      <h2 className="section-title">2️⃣ Example: Flexible Grid</h2>
      <CodeCopyBlock
        code={`.grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 10px;
}

.grid div {
  background: #06b6d4;
  color: white;
  text-align: center;
  padding: 20px;
}`}
      />
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
          gap: "10px",
          marginBottom: "2rem",
        }}
      >
        <div style={{ background: "#06b6d4", color: "white", padding: "20px" }}>
          Box 1
        </div>
        <div style={{ background: "#06b6d4", color: "white", padding: "20px" }}>
          Box 2
        </div>
        <div style={{ background: "#06b6d4", color: "white", padding: "20px" }}>
          Box 3
        </div>
      </div>

      {/* Example 3: Fluid Image */}
      <h2 className="section-title">3️⃣ Example: Fluid Image</h2>
      <CodeCopyBlock
        code={`img {
  max-width: 100%;
  height: auto;
}`}
      />
      <p>
        A fluid image scales automatically to fit within its parent container,
        keeping the aspect ratio.
      </p>

      {/* Next Steps */}
      <div
        style={{
          background: "linear-gradient(135deg, #fef9c3, #fde047)",
          padding: "1.2rem",
          paddingLeft: "35px",
          borderRadius: "12px",
          marginTop: "2rem",
        }}
      >
        <h2 style={{ color: "#854d0e" }}>🚀 Next Steps</h2>
        <ul className="tutorial-list-points">
          <li>Practice with media queries for different breakpoints.</li>
          <li>Experiment with CSS Grid and Flexbox for adaptive layouts.</li>
          <li>
            Use fluid typography (with <code>clamp()</code> or <code>vw</code>{" "}
            units).
          </li>
          <li>
            Test your designs across devices (desktop, tablet, mobile) using
            browser dev tools.
          </li>
        </ul>
      </div>
    </div>
  );
}
